import React, { createContext, useEffect, useState } from 'react'
import './i18n';  //// import i18n (needs to be bundled ;)) 
import { useTranslation } from 'react-i18next';

import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

import { useAppCtx } from './app-ctx';

import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

import LargeItemDropdown from './book-package-select-v2';
import { filterBoardingsForGuest } from './utils/boardings';
import { dateOfBirthFromAge, ageFromDateOfBirth } from './utils/guest-details';
import './book-guest-selector.css';

// Age options for the per-guest Age dropdown (1..100, matching the previous
// number input range). The current value is always offered as an option so an
// out-of-range age is never lost in the select.
const AGE_OPTIONS = Array.from({ length: 100 }, (_, i) => i + 1);

/** Date object to 'YYYY-MM-DD' (local timezone). */
const toIsoDate = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 'YYYY-MM-DD' string to a local-timezone Date object (avoids the UTC
    parsing of new Date(iso), which shifts the day for negative offsets). */
const isoToDate = (iso) => {
    const [y, m, d] = String(iso).split('-').map(Number);
    return new Date(y, m - 1, d);
}

/**
 * Date field backed by a react-calendar popup.
 *
 * Replaces the native <input type="date"> (whose display format follows the
 * browser language, not the site locale): the field shows the date formatted
 * with the current i18n locale (en / en-US are mapped to en-GB, see
 * input-date-range-2.js / criteria-date-range.js) and opens a <Calendar>
 * on click. A light monochrome calendar icon (img/icon-calendar.svg, applied
 * via the .guest-date-icon CSS background) is rendered on the right side of
 * the field (also clickable) as a visual affordance for the popup.
 * The value and the onChange callback keep the ISO 'YYYY-MM-DD'
 * format used by the guest state.
 *
 * Props: className (field classes, incl. 'guest-dob'/'guest-passport-expire'
 * and 'error'), value (ISO string or ''), onChange (iso string callback),
 * minDate/maxDate (Date|null, passed to the Calendar), placeholder (optional
 * override; defaults to a locale-dependent 'DD/MM/YYYY'-style pattern
 * derived from the current i18n locale).
 */
const GuestDateInput = ({className, value, onChange, minDate = null, maxDate = null, placeholder = ''}) => {
    const { t, i18n } = useTranslation();
    const [menuIsOpen, setMenuIsOpen] = useState(false);

    const locale = (i18n.language === 'en' || i18n.language === 'en-US') ? 'en-GB' : i18n.language;

    const formatDate = (iso) => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso ?? ''))) return '';
        const d = isoToDate(iso);
        if (Number.isNaN(d.getTime())) return '';
        return new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(d);
    }

    // Locale-dependent placeholder for empty date fields: format a sample date
    // (31 December 2027 — month '12' and day '31' are two-digit in every
    // locale) with the current locale and replace its parts with DD/MM/YYYY
    // tokens, keeping the locale's own order and separators
    // (e.g. '31/12/2027' -> 'DD/MM/YYYY' for en-GB, '31.12.2027' -> 'DD.MM.YYYY'
    // for uk, '12/31/27' -> 'MM/DD/YYYY' for en). Locales rendering a two-digit
    // year get it expanded to YYYY too.
    const localePlaceholder = (() => {
        const sample = new Intl.DateTimeFormat(locale, { dateStyle: 'short' })
            .format(new Date(2027, 11, 31)); // 31 December 2027
        return sample
            .replace('2027', 'YYYY')
            .replace('27', 'YYYY')  // two-digit year locales (e.g. uk, de)
            .replace('12', 'MM')
            .replace('31', 'DD');
    })();

    const handleCalendarChange = (date) => {
        if (!date) return;
        onChange(toIsoDate(date));
        setMenuIsOpen(false);
    }

    return (
        <div className={`guest-date-input ${menuIsOpen ? 'open' : ''}`}>
            <input type="text" readOnly className={`${className} input-text`}
                value={formatDate(value)} placeholder={placeholder || localePlaceholder}
                title={t('Click to pick a date')}
                onClick={() => setMenuIsOpen(!menuIsOpen)} />
            <span className="guest-date-icon" aria-hidden="true"
                title={t('Click to pick a date')}
                onClick={() => setMenuIsOpen(!menuIsOpen)}></span>
            {menuIsOpen && (
                <Calendar className="guest-date-calendar"
                    value={value ? isoToDate(value) : null}
                    onChange={handleCalendarChange}
                    locale={locale}
                    minDate={minDate}
                    maxDate={maxDate} />
            )}
        </div>
    );
}

/**
 * _guests:                                                |
 *      array: [ {id, age, boardId, firstName, secondName, phone, dob,
 *                passport, passportExpire}, ...]
 * boardings
 *      array: [ {board_id, name, description, lang, db_row_id, resort_id }, ... ]
 * treatmentFrom
 *      number|null resort `treatment_from` property: boarding codes [4..6]
 *      are visible only for guests with age >= treatmentFrom
 * guestErrors
 *      object|null: { [guestId]: { firstName, secondName, dob, passport, passportExpire }, ... }
 */
const BookGuestSelector = ({className, _guests, boardings, treatmentFrom = null, 
                        onGuestsListChange, guestErrors = null, defaultCountry = 'UA'}) => {
    const { t } = useTranslation();

    console.log('BookGuestSelector().Entry: _guests=', JSON.stringify(_guests));
    //console.log('BookGuestSelector().Entry: boardings=', JSON.stringify(boardings));

    const [guests, setGuests] = useState(_guests);

    const guestError = (guest) => (guestErrors && guestErrors[guest.id]) || null;

    // When the guest age changes the date of birth (guessed automatically
    // from the age field) and the allowed packages must be updated too.
    const applyAge = (guest, age) => {
        const next = { ...guest, age, dob: dateOfBirthFromAge(age) };
        const allowed = filterBoardingsForGuest(boardings, age, treatmentFrom);
        if (!allowed.find((b) => parseInt(b.board_id, 10) === parseInt(next.boardId, 10))) {
            next.boardId = allowed.length > 0 ? allowed[0].board_id : null;
        }
        return next;
    }

    handleChangeGuestAge = (guestId, age) => {
        setGuests(guests.map(g => g.id === guestId ? applyAge(g, age) : g));
    }

    // When the user changes the date of birth the age field is updated too
    // (same package re-validation as on a direct age change).
    // The picked DOB must be kept as-is: applyAge() must not overwrite it
    // with dateOfBirthFromAge(age) (it would snap any picked date back to
    // the age-derived one).
    handleChangeGuestDob = (guestId, dob) => {
        setGuests(guests.map(g => {
            if (g.id !== guestId) return g;
            const derivedAge = ageFromDateOfBirth(dob);
            if (derivedAge === null) {
                // Invalid or cleared value: keep the age, let validation complain.
                return { ...g, dob: dob ?? '' };
            }
            const next = { ...applyAge({ ...g, dob: g.dob }, derivedAge), dob };
            return next;
        }));
    }

    handleChangeGuestField = (guestId, field, value) => {
        setGuests(guests.map(g => g.id === guestId ? { ...g, [field]: value } : g));
    }

    handleChangeGuestBoard = (guestId, boardId) => {
        console.log(`BookGuestSelector.handleChangeGuestBoard(): guestId=${guestId}, boardId=${boardId}`);
        setGuests(guests.map(g => g.id === guestId ? {...g, boardId} : g));
    }
    handleDeleteGuest = (guestId) => {
        console.log(`BookGuestSelector.handleDeleteGuest(): guestId=${guestId}`);
        if (guests.length > 1) {
            setGuests(guests.filter(g => g.id !== guestId));
        }
    }
    handleAddGuest = (event) => {
        event.preventDefault();
        event.stopPropagation();
        console.log('BookGuestSelector.handleAddGuest()');

        const allowed = filterBoardingsForGuest(boardings, 21, treatmentFrom);
        const newGuests = guests
            .map((g, i) => ({...g, id: i + 1}))
            .concat([{id: guests.length + 1, age: 21, meals: 1, treatment: false,
                firstName: '', secondName: '', phone: '',
                dob: dateOfBirthFromAge(21),
                passport: '', passportExpire: '',
                boardId: allowed.length > 0 ? allowed[0].board_id : null}]);
        setGuests(newGuests);
    }
    
    useEffect(() => {
        console.log('BookGuestSelector.useEffect[guests]: triggering onGuestsListChange', JSON.stringify(guests));
        onGuestsListChange(guests);
    }, [guests]);   
    

    return (
        <>
            <div className={`${className} guest-list-wrapper`} >
                <div className="guest-list">
                    {guests.map((guest) => {
                        const err = guestError(guest) || {};
                        return (
                            <div className='guest-wrapper' key={guest.id}> 
                                <span className="guest-id-col">
                                    {/* Invisible spacer repeats the "Age" label
                                        text box height so the circle is aligned
                                        with the Age select, not with the label
                                        text. The circle keeps its own column. */}
                                    <label className='guest-col-label guest-id-col-label'>
                                        <span className='guest-col-label-text guest-id-spacer' aria-hidden='true'>&nbsp;</span>
                                        <span className="guest-age-id guest-id">
                                            {guest.id}
                                        </span>
                                    </label>
                                </span>
                                <span className="guest-age-col">
                                    <label className='guest-col-label'>
                                        <span className='guest-col-label-text'>{t('Age')}</span>
                                        <select value={guest.age}
                                                onChange={(e) => handleChangeGuestAge(guest.id, parseInt(e.target.value, 10))}
                                                className='guest-age'>
                                            {AGE_OPTIONS.includes(parseInt(guest.age, 10)) ? null : (
                                                <option value={guest.age}>{guest.age}</option>
                                            )}
                                            {AGE_OPTIONS.map((a) => (
                                                <option key={a} value={a}>{a}</option>
                                            ))}
                                        </select>
                                    </label>
                                </span>
                                <span className="guest-meals-col">
                                    <label className='guest-col-label'>
                                        <span className='guest-col-label-text'>{t('Package')}</span>
                                        <LargeItemDropdown value={guest.boardId}
                                                items={filterBoardingsForGuest(boardings, guest.age, treatmentFrom)}
                                                onChange={(e) => handleChangeGuestBoard(guest.id, parseInt(e.target.value))}>
                                        </LargeItemDropdown>
                                    </label>
                                </span>
                                <span className="guest-actions-col">
                                    <span className='button delete-button' onClick={(e) => handleDeleteGuest(guest.id)}></span>
                                </span>
                                {/*
                                    Per-guest personal details:
                                    - First Name, Second Name, Phone number,
                                      Date of Birth - in one line
                                    - Passport number, Passport expire date - in one line
                                    - Date of Birth is guessed automatically from the Age
                                      field; changing it updates the Age field too.
                                    - The guest phone is optional and is NOT validated.
                                */}
                                <div className='guest-details'>
                                    <div className='guest-details-line guest-details-names'>
                                        <label className='guest-field-label'>
                                            <span className='guest-field-label-text'>{t('First Name')}</span>
                                            <input className={`input-text guest-first-name ${err.firstName ? 'error' : ''}`}
                                                type="text"
                                                value={guest.firstName ?? ''}
                                                onChange={(e) => handleChangeGuestField(guest.id, 'firstName', e.target.value)} />
                                            {err.firstName && <span className='error-text'>{err.firstName}</span>}
                                        </label>
                                        <label className='guest-field-label'>
                                            <span className='guest-field-label-text'>{t('Second Name')}</span>
                                            <input className={`input-text guest-second-name ${err.secondName ? 'error' : ''}`}
                                                type="text"
                                                value={guest.secondName ?? ''}
                                                onChange={(e) => handleChangeGuestField(guest.id, 'secondName', e.target.value)} />
                                            {err.secondName && <span className='error-text'>{err.secondName}</span>}
                                        </label>
                                        <label className='guest-field-label'>
                                            <span className='guest-field-label-text'>{t('Phone number')}</span>
                                            <div className='phone-input-wrapper guest-phone'>
                                                <PhoneInput international className="" defaultCountry={defaultCountry}
                                                    placeholder="+380 XX XXX XXXX"
                                                    value={guest.phone ?? ''}
                                                    onChange={(v) => handleChangeGuestField(guest.id, 'phone', v)} />
                                            </div>
                                        </label>
                                        <label className='guest-field-label'>
                                            <span className='guest-field-label-text'>{t('Date of Birth')}</span>
                                            <GuestDateInput className={`guest-dob ${err.dob ? 'error' : ''}`}
                                                value={guest.dob ?? ''}
                                                maxDate={new Date()} // birth date cannot be in the future
                                                onChange={(iso) => handleChangeGuestDob(guest.id, iso)} />
                                            {err.dob && <span className='error-text'>{err.dob}</span>}
                                        </label>
                                    </div>
                                    <div className='guest-details-line guest-details-passport'>
                                        <label className='guest-field-label'>
                                            <span className='guest-field-label-text'>{t('Passport number')}</span>
                                            <input className={`input-text guest-passport ${err.passport ? 'error' : ''}`}
                                                type="text"
                                                value={guest.passport ?? ''}
                                                onChange={(e) => handleChangeGuestField(guest.id, 'passport', e.target.value)} />
                                            {err.passport && <span className='error-text'>{err.passport}</span>}
                                        </label>
                                        <label className='guest-field-label'>
                                            <span className='guest-field-label-text'>{t('Passport expire date')}</span>
                                            <GuestDateInput className={`guest-passport-expire ${err.passportExpire ? 'error' : ''}`}
                                                value={guest.passportExpire ?? ''}
                                                minDate={new Date()} // expiry date cannot be in the past
                                                onChange={(iso) => handleChangeGuestField(guest.id, 'passportExpire', iso)} />
                                            {err.passportExpire && <span className='error-text'>{err.passportExpire}</span>}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className='add-button-wrapper'>
                    <button className='button add-button is-primary' onClick={handleAddGuest}>{t('Add guest')}</button>
                </div>
            </div>
        </>
    )
}

export default BookGuestSelector;